/**
 * Recently viewed products: a handle history kept in localStorage.
 *
 * `<recently-viewed-tracker>` (PDP) pushes the current handle onto the history;
 * `<recently-viewed-products>` (any template) reads it back and renders the cards.
 * Cards are fetched from the `recently-viewed-card` section rather than built in
 * JS, so their markup stays in Liquid and matches every other product card.
 */
const RECENTLY_VIEWED_KEY = 'recently-viewed-products';
const RECENTLY_VIEWED_LIMIT = 12;
const RECENTLY_VIEWED_CARD_SECTION = 'recently-viewed-card';

const readRecentlyViewed = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY));
    return Array.isArray(stored) ? stored.filter((handle) => typeof handle === 'string') : [];
  } catch {
    return [];
  }
};

const writeRecentlyViewed = (handles) => {
  try {
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(handles));
  } catch {
    // Private mode or a full quota: the history is a nice-to-have, never a blocker.
  }
};

const pushRecentlyViewed = (handle) => {
  if (!handle) return;

  const history = readRecentlyViewed().filter((stored) => stored !== handle);
  history.unshift(handle);
  writeRecentlyViewed(history.slice(0, RECENTLY_VIEWED_LIMIT));
};

if (!customElements.get('recently-viewed-tracker')) {
  customElements.define(
    'recently-viewed-tracker',
    class RecentlyViewedTracker extends HTMLElement {
      connectedCallback() {
        pushRecentlyViewed(this.dataset.productHandle);
      }
    }
  );
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-recently-viewed-list]');
        if (!this.list) return;

        // On a PDP the tracker has already stored the current product; showing it
        // back to the customer inside "recently viewed" would be noise.
        const excludeHandle = this.dataset.excludeHandle;
        const limit = Number(this.dataset.productsToShow) || 8;
        const handles = readRecentlyViewed()
          .filter((handle) => handle !== excludeHandle)
          .slice(0, limit);

        if (!handles.length) {
          this.remove();
          return;
        }

        this.render(handles);
      }

      async render(handles) {
        const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
        const slides = cards.filter(Boolean);

        // Every handle is stale (products unpublished or deleted) — nothing to show.
        if (!slides.length) {
          this.remove();
          return;
        }

        this.list.append(...slides);
        this.hidden = false;
        this.initSlider();
      }

      /**
       * Resolves to a ready slide, or to null when the product no longer renders —
       * a handle can outlive the product it points at.
       */
      async fetchCard(handle) {
        try {
          const response = await fetch(
            `${window.Shopify.routes.root}products/${handle}?section_id=${RECENTLY_VIEWED_CARD_SECTION}`
          );
          if (!response.ok) return null;

          const markup = await response.text();
          const card = new DOMParser().parseFromString(markup, 'text/html').querySelector('.card-product-wrapper');
          if (!card) return null;

          const slide = document.createElement('div');
          slide.className = 'swiper-slide recently-viewed__item';
          slide.appendChild(card);
          return slide;
        } catch {
          return null;
        }
      }

      // The slides land after the DOMContentLoaded slider scan, so this element
      // initializes its own Swiper the way <product-recommendations> does.
      initSlider() {
        const slider = this.querySelector('[data-slider-selector]');
        if (!slider) return;

        if (typeof Swiper !== 'undefined') {
          new SwiperConfigurator(slider);
          return;
        }

        SwiperConfigurator.appendSwiperFiles()
          .then(() => new SwiperConfigurator(slider))
          .catch((error) => console.error('Failed to load Swiper files:', error));
      }
    }
  );
}
